import React, { useEffect, useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import './Map.css';

const darkMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#1f2a37' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4a5568' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }]
  }
];

const lightMapStyles = [
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c0d6eb' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#c3e6a3' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#f5d480' }]
  }
];

const TrackMarker = ({ children }) => children;

const Map = ({
  points = [],
  path = [],
  darkMode,
  selectedIndex = null,
  onSelectIndex
}) => {
  const mapRef = useRef();
  const mapsApiRef = useRef();
  const polylineRef = useRef();
  const [zoom, setZoom] = useState(5);
  const [center, setCenter] = useState({
    lat: -14.8227361,
    lng: -54.1549677
  });

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (!map || !maps || !path.length) {
      return undefined;
    }

    polylineRef.current = new maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#1c4220',
      strokeOpacity: 0.95,
      strokeWeight: 4,
      map
    });

    if (path.length === 1) {
      map.setCenter(path[0]);
      map.setZoom(14);
      setCenter(path[0]);
      setZoom(14);
    } else {
      const bounds = new maps.LatLngBounds();
      path.forEach(point => bounds.extend(point));
      map.fitBounds(bounds, 64);
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [path]);

  useEffect(() => {
    if (selectedIndex == null || !points[selectedIndex] || !mapRef.current) {
      return;
    }

    const data = points[selectedIndex].properties.data;
    const nextCenter = { lat: data.lat, lng: data.lng };
    mapRef.current.panTo(nextCenter);
    setCenter(nextCenter);

    const currentZoom = mapRef.current.getZoom?.() ?? zoom;
    if (currentZoom < 12) {
      mapRef.current.setZoom(13);
      setZoom(13);
    }
  }, [selectedIndex, points]);

  const mapOptions = isDark => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControlOptions: {
      style: 2,
      position: 3
    }
  });

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={{
          lat: -14.8227361,
          lng: -54.1549677
        }}
        center={center}
        zoom={zoom}
        defaultZoom={4}
        options={mapOptions(darkMode)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          mapRef.current = map;
          mapsApiRef.current = maps;

          if (path.length && maps) {
            if (polylineRef.current) {
              polylineRef.current.setMap(null);
            }
            polylineRef.current = new maps.Polyline({
              path,
              geodesic: true,
              strokeColor: '#1c4220',
              strokeOpacity: 0.95,
              strokeWeight: 4,
              map
            });

            if (path.length === 1) {
              map.setCenter(path[0]);
              map.setZoom(14);
            } else if (path.length > 1) {
              const bounds = new maps.LatLngBounds();
              path.forEach(point => bounds.extend(point));
              map.fitBounds(bounds, 64);
            }
          }
        }}
        onChange={({ zoom: nextZoom, center: nextCenter }) => {
          setZoom(nextZoom);
          setCenter(nextCenter);
        }}
      >
        {points.map((point, index) => {
          const [lng, lat] = point.geometry.coordinates;
          const data = point.properties.data;
          const isSelected = index === selectedIndex;
          const markerClass = [
            'track-marker',
            data.isStart ? 'track-marker--start' : '',
            data.isEnd ? 'track-marker--end' : '',
            isSelected ? 'track-marker--selected' : ''
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <TrackMarker key={point.properties.id} lat={lat} lng={lng}>
              <button
                type="button"
                className={markerClass}
                onClick={() => onSelectIndex && onSelectIndex(index)}
                title={`Ponto ${data.order}`}
              >
                {data.order}
              </button>
            </TrackMarker>
          );
        })}
      </GoogleMapReact>
    </div>
  );
};

export default Map;
